import React, { useState, useRef, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaQrcode, FaSearch, FaCheckCircle, FaTimesCircle,
  FaUserCircle, FaClock, FaSignOutAlt, FaVideo, FaVideoSlash,
  FaIdBadge, FaCertificate, FaBriefcase, FaCalendarAlt, FaMapMarkerAlt,
  FaPlay, FaStop, FaCommentAlt, FaExclamationTriangle, FaBars, FaTimes, FaTag
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import StatusBadge from '../../shared/JobStatusBadge';
import JobCalendar from '../../shared/JobCalendar';
import UserRoleBadge from '../../shared/UserRoleBadge';
import '../../shared/shared.css';
import jsQR from 'jsqr';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import '../Contractor/ContractorDashboard.css';
import './BuildingManagerDashboard.css';

const BuildingManagerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scan');
  const [isScanning, setIsScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scannedPerson, setScannedPerson] = useState(null);
  const [personActiveJobs, setPersonActiveJobs] = useState([]);
  const [checkInReason, setCheckInReason] = useState('');
  const [selectedJobForSignIn, setSelectedJobForSignIn] = useState(null);
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [todayJobs, setTodayJobs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [selectedJobForForm, setSelectedJobForForm] = useState(null);
  const [complaintText, setComplaintText] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [userCode, setUserCode] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const role = getUserRole();
    if (!['building_manager', 'staff', 'admin', 'super_admin'].includes(role)) {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchProperties();
    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers });
      if (res.ok) {
        const d = await res.json();
        setUserCode(d.userCode || null);
      }
    } catch (e) { /* silent */ }
  };

  const fetchJobDetail = async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${id}`), { headers });
      if (res.ok) setSelectedJob(await res.json());
      else toast.error('Failed to load job');
    } catch (e) { toast.error('Failed to load job'); }
  };

  useEffect(() => {
    if (selectedProperty) {
      fetchCheckInHistory();
      fetchTodayJobs();
      fetchJobs();
      fetchCalendar();
      fetchFeedback();
      fetchComplaints();
    }
  }, [selectedProperty]);

  const fetchJobs = async () => {
    if (!selectedProperty) return;
    setJobsLoading(true);
    try {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      const to = new Date(now);
      to.setDate(to.getDate() + 90);
      const res = await fetch(getApiEndpoint(`/api/jobs?property_id=${selectedProperty}&from=${from.toISOString().split('T')[0]}&to=${to.toISOString().split('T')[0]}`), { headers });
      if (res.ok) setJobs(await res.json());
    } catch (e) { /* silent */ }
    finally { setJobsLoading(false); }
  };

  useEffect(() => { return () => stopScanning(); }, []);

  // Attach stream to video after it mounts (video only renders when isScanning=true)
  useEffect(() => {
    if (!isScanning || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.muted = true; // Required for autoplay in most browsers
    const onReady = () => scanFrame();
    video.onloadedmetadata = onReady;
    video.play().catch(() => {
      // Fallback: start scanning anyway after a delay
      setTimeout(onReady, 500);
    });
  }, [isScanning]);

  const fetchProperties = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/building-manager/properties'), { headers });
      if (res.ok) {
        const data = await res.json();
        setProperties(data);
        if (data.length > 0) {
          setSelectedProperty(data[0].id);
          if (data.length === 1) {
            setActiveTab('scan');
          }
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || err.error || 'Failed to load properties');
      }
    } catch (e) {
      console.error('Failed to fetch properties:', e);
      toast.error('Failed to load your building');
    }
  };

  const fetchCheckInHistory = async () => {
    if (!selectedProperty) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(getApiEndpoint(`/api/building-manager/properties/${selectedProperty}/checkins?date=${today}`), { headers });
      if (res.ok) setCheckInHistory(await res.json());
    } catch (e) { console.error('Failed to fetch check-in history:', e); }
  };

  const fetchTodayJobs = async () => {
    if (!selectedProperty) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(getApiEndpoint(`/api/jobs?property_id=${selectedProperty}&from=${today}&to=${today}`), { headers });
      if (res.ok) setTodayJobs(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchCalendar = async () => {
    if (!selectedProperty) return;
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const res = await fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}&property_id=${selectedProperty}`), { headers });
      if (res.ok) setCalendarEvents(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchFeedback = async () => {
    if (!selectedProperty) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/building-manager/properties/${selectedProperty}/feedback`), { headers });
      if (res.ok) setFeedback(await res.json());
    } catch (e) { /* silent */ }
  };

  const fetchComplaints = async () => {
    if (!selectedProperty) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/building-manager/properties/${selectedProperty}/complaints`), { headers });
      if (res.ok) setComplaints(await res.json());
    } catch (e) { /* silent */ }
  };

  const lookupPerson = useCallback(async (qrData) => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/users/lookup/${encodeURIComponent(qrData)}`), { headers });
      if (res.ok) {
        const data = await res.json();
        setScannedPerson(data.person);
        setPersonActiveJobs(data.activeJobs || []);
        toast.success(`Found: ${data.person.name}`);
        const propertyJobs = (data.activeJobs || []).filter(j => j.property_id === selectedProperty);
        if (propertyJobs.length === 1) {
          setSelectedJobForSignIn(propertyJobs[0]);
          setCheckInReason(propertyJobs[0].title);
        }
      } else {
        setScannedPerson(null);
        setPersonActiveJobs([]);
        toast.error('Person not found in system');
      }
    } catch (e) {
      toast.error('Failed to look up person');
    } finally { setLoading(false); }
  }, [selectedProperty]);

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      setIsScanning(true);
      // Video element is not in DOM yet - useEffect will attach stream after render
    } catch (e) {
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopScanning = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setIsScanning(false);
  };

  const scanFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code) { stopScanning(); lookupPerson(code.data); return; }
    }
    animFrameRef.current = requestAnimationFrame(scanFrame);
  };

  const handleSearch = (e) => { e.preventDefault(); if (searchQuery.trim()) lookupPerson(searchQuery.trim()); };

  const handleCheckIn = async () => {
    if (!scannedPerson || !checkInReason.trim()) { toast.error('Please provide a reason for the visit'); return; }
    if (!selectedProperty) { toast.error('Please select a property'); return; }
    try {
      const res = await fetch(getApiEndpoint('/api/users/checkin'), {
        method: 'POST', headers,
        body: JSON.stringify({
          personId: scannedPerson.cloudSafeId || scannedPerson.email,
          name: scannedPerson.name,
          propertyId: selectedProperty,
          reason: checkInReason,
          company: scannedPerson.company,
          personRole: scannedPerson.role,
          inductionStatus: scannedPerson.inductionStatus,
          certificationValid: scannedPerson.certificationStatus === 'Valid'
        })
      });
      if (res.ok) {
        toast.success(`${scannedPerson.name} checked in successfully`);
        if (selectedJobForSignIn) {
          try {
            await fetch(getApiEndpoint(`/api/jobs/${selectedJobForSignIn.id}/sign-in`), {
              method: 'POST', headers,
              body: JSON.stringify({ user_id: scannedPerson.id })
            });
            toast.success(`Job "${selectedJobForSignIn.title}" started!`);
          } catch (e) { /* best-effort */ }
        }
        setScannedPerson(null);
        setPersonActiveJobs([]);
        setCheckInReason('');
        setSelectedJobForSignIn(null);
        fetchCheckInHistory();
        fetchTodayJobs();
      } else {
        toast.error('Failed to record check-in');
      }
    } catch (e) {
      toast.error('Failed to record check-in');
    }
  };

  const handleCheckOut = async (checkInId, entry) => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/checkout'), {
        method: 'POST', headers,
        body: JSON.stringify({ checkInId })
      });
      if (res.ok) {
        toast.success('Checked out successfully');
        if (entry?.person_id) {
          const personData = await fetch(getApiEndpoint(`/api/users/lookup/${encodeURIComponent(entry.person_id)}`), { headers });
          if (personData.ok) {
            const pd = await personData.json();
            const activeJobsHere = (pd.activeJobs || []).filter(j => j.property_id === selectedProperty && j.status === 'active' && !j.signed_out_at);
            for (const job of activeJobsHere) {
              try {
                const so = await fetch(getApiEndpoint(`/api/jobs/${job.id}/sign-out`), {
                  method: 'POST', headers,
                  body: JSON.stringify({ user_id: pd.person.id })
                });
                // Last one off site: the job is NOT completed automatically any more — the
                // contractor is notified to complete it with the customer sign-off.
                const soBody = so.ok ? await so.json().catch(() => ({})) : {};
                if (soBody.allSignedOut) toast.info(`Everyone has signed out of "${job.title || 'the job'}" — the contractor has been asked to complete it.`);
              } catch (e) { /* best-effort */ }
            }
          }
        }
        fetchCheckInHistory();
        fetchTodayJobs();
      }
    } catch (e) { toast.error('Failed to check out'); }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) { toast.error('Please enter your feedback'); return; }
    if (!selectedProperty) return;
    const job = selectedJobForForm ? jobs.find(j => j.id === selectedJobForForm) : null;
    try {
      const res = await fetch(getApiEndpoint(`/api/building-manager/properties/${selectedProperty}/feedback`), {
        method: 'POST', headers,
        body: JSON.stringify({
          feedback_text: feedbackText.trim(),
          rating: feedbackRating || null,
          job_id: selectedJobForForm || null,
          contractor_user_id: job?.contractor_user_id || null
        })
      });
      if (res.ok) {
        toast.success('Feedback submitted');
        setFeedbackText('');
        setFeedbackRating(0);
        setSelectedJobForForm(null);
        fetchFeedback();
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (e) { toast.error('Failed to submit feedback'); }
  };

  const handleSubmitComplaint = async (e) => {
    e.preventDefault();
    if (!complaintText.trim()) { toast.error('Please describe the complaint'); return; }
    if (!selectedProperty) return;
    const job = selectedJobForForm ? jobs.find(j => j.id === selectedJobForForm) : null;
    try {
      const res = await fetch(getApiEndpoint(`/api/building-manager/properties/${selectedProperty}/complaints`), {
        method: 'POST', headers,
        body: JSON.stringify({
          complaint_text: complaintText.trim(),
          job_id: selectedJobForForm || null,
          contractor_user_id: job?.contractor_user_id || null
        })
      });
      if (res.ok) {
        toast.success('Complaint raised');
        setComplaintText('');
        setSelectedJobForForm(null);
        fetchComplaints();
      } else {
        toast.error('Failed to raise complaint');
      }
    } catch (e) { toast.error('Failed to raise complaint'); }
  };

  const handleLogout = () => { clearToken(); localStorage.removeItem('userRole'); navigate('/login'); toast.success('Logged out'); };

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const getValidationStatus = (person) => {
    if (!person) return { valid: false, label: 'Unknown', color: '#6b7280' };
    const certValid = person.certificationStatus === 'Valid';
    const inductionOk = person.inductionStatus === 'completed';
    if (certValid && inductionOk) return { valid: true, label: 'Cleared for Entry', color: '#059669' };
    if (!inductionOk) return { valid: false, label: 'Induction Required', color: '#dc2626' };
    return { valid: false, label: 'Certification Issue', color: '#d97706' };
  };

  const validation = getValidationStatus(scannedPerson);
  const activeOnSite = checkInHistory.filter(c => c.status === 'checked_in');

  if (properties.length === 0 && !loading) {
    return (
      <div className="contractor-dashboard bm-dashboard">
        <header className="contractor-header">
          <div className="header-left">
            <img src={cloudsafeLogo} alt="CloudSafe" className="header-logo" />
            <div className="header-title-group">
              <h1>Building Manager</h1>
            </div>
          </div>
          <div className="header-right">
            <UserRoleBadge />
            <button className="header-logout" onClick={handleLogout}><FaSignOutAlt /></button>
          </div>
        </header>
        <div className="bm-empty-state">
          <FaUserCircle className="bm-empty-icon" />
          <h2>No building assigned</h2>
          <p>Your property owner or facility manager needs to add you to a building in the property settings using your Building Manager ID.</p>
          <p className="bm-empty-hint">You can find your 6-digit CloudSafe ID in Account Settings.</p>
          <Link to="/account-settings" className="bm-btn bm-btn-primary">Account Settings</Link>
          <button onClick={handleLogout} className="bm-btn bm-btn-outline">Log out</button>
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const jobsToday = jobs.filter(j => ['approved', 'active'].includes(j.status) && j.scheduled_date?.split('T')[0] === today);
  const jobsUpcoming = jobs.filter(j => ['approved', 'active', 'invited'].includes(j.status) && j.scheduled_date?.split('T')[0] > today);
  const jobsPast = jobs.filter(j => j.status === 'completed');

  const sidebarSections = [
    {
      items: [
        { id: 'scan', label: 'Sign In/Out', icon: FaQrcode },
        { id: 'jobs', label: 'Jobs', icon: FaBriefcase, badge: jobsToday.length || null },
        { id: 'calendar', label: 'Calendar', icon: FaCalendarAlt },
        { id: 'onsite', label: 'On Site', icon: FaUserCircle, badge: activeOnSite.length || null },
        { id: 'feedback-complaints', label: 'Feedback & Complaints', icon: FaCommentAlt },
      ]
    },
    {
      label: 'Tools',
      items: [
        { id: 'asset-tagger', label: 'Asset Tagger', icon: FaTag, link: '/contractor/asset-tagger' },
      ]
    },
  ];
  const sidebarItems = sidebarSections.flatMap(s => s.items);

  const recentJobsForForm = [...jobsToday, ...jobsUpcoming, ...jobsPast].slice(0, 25);

  return (
    <div className="contractor-dashboard bm-dashboard">
      <header className="contractor-header bm-header-override">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="header-logo" />
          <div className="header-title-group">
            <h1>Building Manager</h1>
          </div>
        </div>
        <div className="header-right">
          <UserRoleBadge />
          {properties.length > 1 && (
            <select className="bm-property-select" value={selectedProperty || ''} onChange={(e) => setSelectedProperty(parseInt(e.target.value))}>
              {properties.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          )}
          {properties.length === 1 && <span className="bm-property-name">{properties[0]?.name}</span>}
          {userCode && (
            <span className="header-company-badge" title="Your Building Manager ID">
              <span className="header-badge-label">ID:</span>
              <span className="header-contractor-id">{userCode}</span>
            </span>
          )}
          <div className="bm-user-menu-wrap">
            <button onClick={() => setShowUserMenu(!showUserMenu)} className="header-user bm-header-user" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <FaUserCircle className="header-user-icon bm-header-user-icon" />
            </button>
            {showUserMenu && (
              <div className="bm-user-dropdown">
                <Link to="/account-settings" onClick={() => setShowUserMenu(false)}>Account</Link>
                <button onClick={() => { setShowUserMenu(false); handleLogout(); }}>Log out</button>
              </div>
            )}
          </div>
          <button className="header-logout" onClick={handleLogout}><FaSignOutAlt /></button>
        </div>
      </header>

      <div className="contractor-layout">
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            {sidebarSections.map((section, si) => (
              <div key={si} className="sidebar-section">
                {section.label && (
                  <div className="sidebar-section-label">{section.label}</div>
                )}
                {section.items.map(item => (
                  item.link ? (
                    <Link
                      key={item.id}
                      to={item.link}
                      className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="sidebar-icon" />
                      <span>{item.label}</span>
                      {item.badge != null && item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                    >
                      <item.icon className="sidebar-icon" />
                      <span>{item.label}</span>
                      {item.badge != null && item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
                    </button>
                  )
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <main className="contractor-main bm-main">
          {selectedProperty && (
            <div className="con-stats-grid bm-stats-grid">
              <div className="con-stat-card" {...clickable(() => setActiveTab('onsite'), { label: 'Open on-site' })}>
                <div className="con-stat-icon bm-icon-onsite"><FaUserCircle /></div>
                <div className="con-stat-content">
                  <span className="con-stat-value">{activeOnSite.length}</span>
                  <span className="con-stat-label">On Site</span>
                </div>
              </div>
              <div className="con-stat-card" {...clickable(() => setActiveTab('onsite'), { label: 'Open on-site' })}>
                <div className="con-stat-icon bm-icon-visits"><FaClock /></div>
                <div className="con-stat-content">
                  <span className="con-stat-value">{checkInHistory.length}</span>
                  <span className="con-stat-label">Visits Today</span>
                </div>
              </div>
              <div className="con-stat-card" {...clickable(() => setActiveTab('jobs'), { label: 'Open jobs' })}>
                <div className="con-stat-icon bm-icon-jobs"><FaBriefcase /></div>
                <div className="con-stat-content">
                  <span className="con-stat-value">{jobsToday.length}</span>
                  <span className="con-stat-label">Jobs Today</span>
                </div>
              </div>
              <div className="con-stat-card" {...clickable(() => setActiveTab('jobs'), { label: 'Open jobs' })}>
                <div className="con-stat-icon bm-icon-upcoming"><FaCalendarAlt /></div>
                <div className="con-stat-content">
                  <span className="con-stat-value">{jobsUpcoming.length}</span>
                  <span className="con-stat-label">Upcoming</span>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'scan' && renderScanTab()}
          {activeTab === 'jobs' && renderJobsTab()}
          {activeTab === 'calendar' && renderCalendarTab()}
          {activeTab === 'onsite' && renderOnSiteTab()}
          {activeTab === 'feedback-complaints' && renderFeedbackComplaintsTab()}
        </main>
      </div>
      {selectedJob && <BMJobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} />}
    </div>
  );

  function renderScanTab() {
    return (
      <div className="bm-scan">
        <div className="bm-scanner-panel">
          <div className="bm-scanner-area">
            {isScanning ? (
              <div className="bm-video-wrap">
                <video ref={videoRef} className="bm-video" playsInline muted autoPlay />
                <canvas ref={canvasRef} className="bm-canvas" />
                <div className="bm-scan-overlay"><div className="bm-scan-frame" /></div>
                <button className="bm-stop-scan" onClick={stopScanning}><FaVideoSlash /> Stop</button>
              </div>
            ) : (
              <div className="bm-start-scan">
                <FaQrcode className="bm-scan-icon" />
                <p>Scan contractor QR badge to verify and sign in</p>
                <button className="bm-start-scan-btn" onClick={startScanning}><FaVideo /> Start Scanner</button>
              </div>
            )}
          </div>
          <form onSubmit={handleSearch} className="bm-search-form">
            <FaSearch className="bm-search-icon" />
            <input type="text" placeholder="Search by name, email, or ID..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bm-search-input" />
            <button type="submit" className="bm-search-btn" disabled={loading}>{loading ? '...' : 'Search'}</button>
          </form>
          {scannedPerson && (
            <div className="bm-person-card">
              <div className={`bm-validation-banner ${validation.valid ? 'valid' : 'invalid'}`} style={{ backgroundColor: validation.color }}>
                {validation.valid ? <FaCheckCircle /> : <FaTimesCircle />}
                <span>{validation.label}</span>
              </div>
              <div className="bm-person-details">
                <FaUserCircle className="bm-person-avatar" />
                <div>
                  <h2>{scannedPerson.name}</h2>
                  <span className="bm-person-role">{scannedPerson.role}</span>
                  {scannedPerson.company && <span className="bm-person-company">{scannedPerson.company}</span>}
                </div>
              </div>
              <div className="bm-person-meta">
                <div className="bm-meta-item"><FaIdBadge /><div><label>CloudSafe ID</label><span>{scannedPerson.cloudSafeId || scannedPerson.email}</span></div></div>
                <div className="bm-meta-item"><FaCertificate /><div><label>Certification</label><span className={scannedPerson.certificationStatus === 'Valid' ? 'valid' : 'invalid'}>{scannedPerson.certificationStatus || 'Unknown'}</span></div></div>
              </div>
              {personActiveJobs.filter(j => j.property_id === selectedProperty).length > 0 && (
                <div className="bm-person-jobs">
                  <h4><FaBriefcase /> Active Jobs</h4>
                  {personActiveJobs.filter(j => j.property_id === selectedProperty).map(j => (
                    <div key={j.id} className={`bm-job-item ${selectedJobForSignIn?.id === j.id ? 'selected' : ''}`} onClick={() => { setSelectedJobForSignIn(j); setCheckInReason(j.title); }}>
                      <span>{j.title}</span>
                      <StatusBadge status={j.status} size="small" />
                    </div>
                  ))}
                </div>
              )}
              <div className="bm-checkin-form">
                <input type="text" placeholder="Reason for visit..." value={checkInReason} onChange={(e) => setCheckInReason(e.target.value)} className="bm-reason-input" />
                <button className={`bm-checkin-btn ${validation.valid ? 'valid' : 'warning'}`} onClick={handleCheckIn} disabled={!checkInReason.trim()}>
                  <FaCheckCircle /> Sign In {selectedJobForSignIn ? '& Start Job' : ''}
                </button>
              </div>
              <button className="bm-clear-btn" onClick={() => { setScannedPerson(null); setCheckInReason(''); setSelectedJobForSignIn(null); setPersonActiveJobs([]); }}>Clear & Scan Next</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderJobsTab() {
    return (
      <div className="bm-jobs">
        <h2 className="bm-section-title">Jobs for this Building</h2>
        {jobsLoading ? (
          <div className="bm-loading-skeleton">
            <div className="con-skeleton con-skeleton-card" />
            <div className="con-skeleton con-skeleton-card" />
            <div className="con-skeleton con-skeleton-card" />
          </div>
        ) : (
          <>
            <h3 className="bm-jobs-subtitle">Today&apos;s Jobs</h3>
            {jobsToday.length > 0 ? jobsToday.map(j => (
              <div key={j.id} className="bm-job-card" {...clickable(() => fetchJobDetail(j.id))}>
                <div className="bm-job-top">
                  <h3>{j.title}</h3>
                  <StatusBadge status={j.status} size="small" />
                </div>
                <div className="bm-job-meta">
                  <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                  <span><FaClock /> {j.area}</span>
                </div>
              </div>
            )) : (
              <div className="bm-jobs-empty"><FaBriefcase /><p>No jobs scheduled for today</p></div>
            )}

            <h3 className="bm-jobs-subtitle" style={{ marginTop: '1.5rem' }}>Upcoming Jobs</h3>
            {jobsUpcoming.length > 0 ? jobsUpcoming.map(j => (
              <div key={j.id} className="bm-job-card" {...clickable(() => fetchJobDetail(j.id))}>
                <div className="bm-job-top">
                  <h3>{j.title}</h3>
                  <StatusBadge status={j.status} size="small" />
                </div>
                <div className="bm-job-meta">
                  <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                  <span><FaCalendarAlt /> {formatDate(j.scheduled_date)}</span>
                </div>
              </div>
            )) : <div className="bm-jobs-empty"><FaCalendarAlt /><p>No upcoming jobs</p></div>}

            <h3 className="bm-jobs-subtitle" style={{ marginTop: '1.5rem' }}>Completed Jobs</h3>
            {jobsPast.length > 0 ? jobsPast.map(j => (
              <div key={j.id} className="bm-job-card" {...clickable(() => fetchJobDetail(j.id))}>
                <div className="bm-job-top">
                  <h3>{j.title}</h3>
                  <StatusBadge status={j.status} size="small" />
                </div>
                <div className="bm-job-meta">
                  <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                  <span><FaCalendarAlt /> {formatDate(j.scheduled_date)}</span>
                  {j.duration_minutes != null && <span><FaClock /> {Math.floor(j.duration_minutes / 60)}h {j.duration_minutes % 60}m</span>}
                </div>
              </div>
            )) : <div className="bm-jobs-empty"><FaCheckCircle /><p>No completed jobs yet</p></div>}
          </>
        )}
      </div>
    );
  }

  function renderCalendarTab() {
    return (
      <div className="bm-calendar">
        <h2 className="bm-section-title">Job Calendar</h2>
        <JobCalendar
          events={calendarEvents}
          onEventClick={(ev) => fetchJobDetail(ev.id)}
          onDateChange={(start, end) => {
            fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}&property_id=${selectedProperty}`), { headers })
              .then(r => r.ok ? r.json() : []).then(setCalendarEvents);
          }}
        />
      </div>
    );
  }

  function renderOnSiteTab() {
    return (
      <div className="bm-onsite">
        <h2 className="bm-section-title">On Site Now ({activeOnSite.length})</h2>
        {activeOnSite.length > 0 ? activeOnSite.map(entry => (
          <div key={entry.id} className="bm-onsite-card">
            <div className="bm-onsite-main">
              <FaUserCircle className="bm-onsite-avatar" />
              <div>
                <span className="bm-onsite-name">{entry.person_name}</span>
                <span className="bm-onsite-detail">{entry.company || entry.person_role} - {entry.reason}</span>
                <span className="bm-onsite-time">In since {formatTime(entry.check_in_time)}</span>
              </div>
            </div>
            <button className="bm-signout-btn" onClick={() => handleCheckOut(entry.id, entry)}><FaStop /> Sign Out</button>
          </div>
        )) : (
          <div className="bm-empty"><FaUserCircle /><p>No one currently on site</p></div>
        )}
        <h2 className="bm-section-title" style={{ marginTop: '2rem' }}>Today&apos;s History</h2>
        {checkInHistory.filter(c => c.status === 'checked_out').map(entry => (
          <div key={entry.id} className="bm-history-item">
            <div><span className="bm-hist-name">{entry.person_name}</span><span className="bm-hist-reason">{entry.reason}</span></div>
            <div className="bm-hist-times">{formatTime(entry.check_in_time)} - {formatTime(entry.check_out_time)}</div>
          </div>
        ))}
      </div>
    );
  }

  function renderFeedbackComplaintsTab() {
    return (
      <div className="bm-feedback-complaints">
        <h2 className="bm-section-title">Feedback & Complaints</h2>
        <p className="bm-fc-intro">Select a job to link your feedback or complaint—makes it easy to track who was on site and what happened.</p>

        <div className="bm-fc-recent-jobs">
          <h3 className="bm-fc-subtitle">Recent Jobs (select to link)</h3>
          {recentJobsForForm.length > 0 ? (
            <div className="bm-fc-job-list">
              {recentJobsForForm.map(j => (
                <div key={j.id} className="bm-fc-job-chip-wrap">
                  <button
                    type="button"
                    className={`bm-fc-job-chip ${selectedJobForForm === j.id ? 'selected' : ''}`}
                    onClick={() => setSelectedJobForForm(prev => prev === j.id ? null : j.id)}
                    title={`${j.title} – ${j.contractor_first_name || ''} ${j.contractor_last_name || ''} – ${formatDate(j.scheduled_date)}`}
                  >
                    <span className="bm-fc-job-title">{j.title}</span>
                    <span className="bm-fc-job-meta">{formatDate(j.scheduled_date)}{j.contractor_first_name ? ` • ${j.contractor_first_name} ${j.contractor_last_name}` : ''}</span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="bm-fc-no-jobs">No recent jobs. Jobs will appear here once scheduled.</p>
          )}
        </div>

        <div className="bm-fc-forms-box">
          <div className="bm-fc-form-section">
            <h3 className="bm-fc-form-title"><FaCommentAlt /> Leave Feedback</h3>
            <form onSubmit={handleSubmitFeedback} className="bm-feedback-form">
              {selectedJobForForm && (
                <p className="bm-fc-linked-job">Linked to: {jobs.find(j => j.id === selectedJobForForm)?.title}</p>
              )}
              <textarea placeholder="Share feedback about the contractor, job, or experience..." value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} className="bm-feedback-textarea" rows={3} />
              <div className="bm-feedback-rating">
                <span>Rating (optional):</span>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" className={`bm-star ${feedbackRating >= n ? 'active' : ''}`} onClick={() => setFeedbackRating(n)}>★</button>
                ))}
              </div>
              <button type="submit" className="bm-btn bm-btn-primary" disabled={!feedbackText.trim()}>Submit Feedback</button>
            </form>
          </div>

          <div className="bm-fc-form-section">
            <h3 className="bm-fc-form-title"><FaExclamationTriangle /> Raise a Complaint</h3>
            <form onSubmit={handleSubmitComplaint} className="bm-complaint-form">
              {selectedJobForForm && (
                <p className="bm-fc-linked-job">Linked to: {jobs.find(j => j.id === selectedJobForForm)?.title}</p>
              )}
              <textarea placeholder="Describe the issue or complaint..." value={complaintText} onChange={(e) => setComplaintText(e.target.value)} className="bm-complaint-textarea" rows={3} />
              <button type="submit" className="bm-btn bm-btn-primary" disabled={!complaintText.trim()}>Submit Complaint</button>
            </form>
          </div>
        </div>

        <div className="bm-fc-recent">
          <h3 className="bm-fc-subtitle">Recent Feedback</h3>
          {feedback.length > 0 ? feedback.slice(0, 8).map(f => (
            <div key={f.id} className="bm-feedback-item">
              <p>{f.feedback_text}</p>
              <div className="bm-feedback-meta">
                {f.job_title && <span className="bm-fc-job-tag">{f.job_title}</span>}
                {f.rating && <span>★ {f.rating}</span>}
                <span>{formatDate(f.created_at)}</span>
              </div>
            </div>
          )) : <div className="bm-empty"><FaCommentAlt /><p>No feedback yet</p></div>}

          <h3 className="bm-fc-subtitle" style={{ marginTop: '1.5rem' }}>Recent Complaints</h3>
          {complaints.length > 0 ? complaints.slice(0, 8).map(c => (
            <div key={c.id} className={`bm-complaint-item status-${c.status}`}>
              <p>{c.complaint_text}</p>
              <div className="bm-complaint-meta">
                {c.job_title && <span className="bm-fc-job-tag">{c.job_title}</span>}
                <span className={`bm-status-badge ${c.status}`}>{c.status}</span>
                <span>{formatDate(c.created_at)}</span>
              </div>
            </div>
          )) : <div className="bm-empty"><FaExclamationTriangle /><p>No complaints yet</p></div>}
        </div>
      </div>
    );
  }
};

/* ─── BM Job Detail Modal (read-only) ─────────────────────────── */
const BMJobDetailModal = ({ job, onClose }) => (
  <div className="cs-modal-overlay" onClick={onClose}>
    <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
      <div className="cs-modal-header">
        <h3>{job.title}</h3>
        <button className="cs-modal-close" onClick={onClose}><FaTimes /></button>
      </div>
      <div className="cs-modal-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <StatusBadge status={job.status} />
          <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{job.area}</span>
        </div>
        {job.description && <p style={{ fontSize: '0.88rem', color: '#374151', marginBottom: '1rem', lineHeight: 1.5 }}>{job.description}</p>}
        <div className="fm-job-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div className="detail-item"><strong>Property:</strong> {job.property_name}</div>
          <div className="detail-item"><strong>Date:</strong> {new Date(job.scheduled_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          {job.contractor_first_name && <div className="detail-item"><strong>Contractor:</strong> {job.contractor_first_name} {job.contractor_last_name}</div>}
          {job.duration_minutes && <div className="detail-item"><strong>Duration:</strong> {Math.floor(job.duration_minutes / 60)}h {job.duration_minutes % 60}m</div>}
        </div>
        {job.staff && job.staff.length > 0 && (
          <div style={{ marginTop: '1.25rem' }}>
            <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Assigned Staff</h4>
            {job.staff.map(s => (
              <div key={s.id} className="fm-staff-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FaUserCircle style={{ color: '#d1d5db', fontSize: '1.2rem' }} />
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.first_name} {s.last_name}</span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block' }}>
                    {s.signed_in_at ? `Signed in ${new Date(s.signed_in_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Not signed in'}
                    {s.signed_out_at && ` — Out ${new Date(s.signed_out_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default BuildingManagerDashboard;
